/**
 * Preferences Modal Component
 * Requirements: 13.1, 13.2, 13.4
 */
import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { X, Loader2, Save, Brain, SlidersHorizontal, ChevronDown, ChevronRight, CreditCard, Shield, Clock, Mail, AlertCircle } from 'lucide-react';
import type { UserPreferences, SubcomponentType } from '../types';
import { DEFAULT_SECTION_ORDER } from '../types';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { PasswordChangeSection } from './preferences/PasswordChangeSection';
import { SubscriptionSection } from './preferences/SubscriptionSection';
import { OrganizationSection } from './preferences/OrganizationSection';
import { GenerationSection } from './preferences/GenerationSection';
import { YearLimitsSection } from './preferences/YearLimitsSection';
import { JobRetentionSection } from './preferences/JobRetentionSection';
import { ForwardingEmailsSection } from './preferences/ForwardingEmailsSection';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PreferencesModal({ isOpen, onClose }: PreferencesModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>({
    default_gen_contact: 'manual',
    default_gen_summary: 'generate',
    default_gen_skills: 'embedding',
    default_gen_highlights: 'generate',
    default_gen_experience: 'embedding',
    default_gen_education: 'manual',
    default_gen_awards: 'omit',
    default_gen_presentations: 'omit',
    default_gen_publications: 'omit',
    default_gen_portfolios: 'omit',
    default_gen_coverletter: 'generate',
    show_year_education: false,
    show_year_awards: false,
    show_year_presentations: false,
    cutoff_year: undefined,
    // Active Phases
    days_queued_before_expired: 90,
    days_generating_before_expired: 90,
    days_ready_before_expired: 90,
    days_applied_before_expired: 90,
    days_followup_before_expired: 90,
    days_negotiation_before_expired: 90,
    days_accepted_before_expired: 365,
    // Terminal Phases
    days_skipped_before_trashed: 10,
    days_errored_before_trashed: 10,
    days_expired_before_trashed: 30,
    days_trash_before_deleted: 30,
  });

  // Accordion state (default closed)
  const [generationSectionOpen, setGenerationSectionOpen] = useState(false);
  const [optionsSectionOpen, setOptionsSectionOpen] = useState(false);
  const [appOptionsSectionOpen, setAppOptionsSectionOpen] = useState(false);
  const [securitySectionOpen, setSecuritySectionOpen] = useState(false);
  const [subscriptionSectionOpen, setSubscriptionSectionOpen] = useState(false);

  // Section Names state (parsed from preferences.section_names JSON string)
  const [sectionNamesMap, setSectionNamesMap] = useState<Record<string, string>>({});

  // Section Order state (parsed from preferences.section_order JSON string)
  const [sectionOrder, setSectionOrder] = useState<SubcomponentType[]>([...DEFAULT_SECTION_ORDER]);

  useEffect(() => {
    if (isOpen) {
      loadPreferences();
    }
  }, [isOpen]);

  const modalRef = useRef<HTMLDivElement>(null);
  useEscapeClose(() => onClose(), isOpen);
  useFocusTrap(modalRef, isOpen);

  async function loadPreferences() {
    setLoading(true);
    try {
      const prefs = await api.getPreferences();
      setPreferences({
        ...preferences,
        ...prefs,
      });

      // Parse section_names JSON
      try {
        const parsed = prefs.section_names ? JSON.parse(prefs.section_names) : {};
        setSectionNamesMap(parsed);
      } catch {
        setSectionNamesMap({});
      }

      // Parse section_order JSON
      try {
        const parsedOrder = prefs.section_order ? JSON.parse(prefs.section_order) as SubcomponentType[] : [];
        setSectionOrder(parsedOrder.length > 0 ? parsedOrder : [...DEFAULT_SECTION_ORDER]);
      } catch {
        setSectionOrder([...DEFAULT_SECTION_ORDER]);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      await api.updatePreferences(preferences);
      onClose();
    } catch (error) {
      console.error('Failed to save preferences:', error);
      const message = error instanceof Error ? error.message : 'Failed to save preferences';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label="Settings">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Settings</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8" role="status" aria-label="Loading preferences">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Resume Sections Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setGenerationSectionOpen(!generationSectionOpen)}
                  aria-expanded={generationSectionOpen}
                  data-help-target="mainapp.user.settings.resumesections"
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <Brain className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Resume Sections</span>
                  </div>
                  {generationSectionOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {generationSectionOpen && (
                  <GenerationSection
                    preferences={preferences}
                    sectionNamesMap={sectionNamesMap}
                    sectionOrder={sectionOrder}
                    onPreferencesChange={setPreferences}
                    onSectionNamesMapChange={setSectionNamesMap}
                    onSectionOrderChange={(newOrder) => {
                      setSectionOrder(newOrder);
                      setPreferences(prev => ({ ...prev, section_order: JSON.stringify(newOrder) }));
                    }}
                  />
                )}
              </div>

              {/* Resume Generation Options Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOptionsSectionOpen(!optionsSectionOpen)}
                  aria-expanded={optionsSectionOpen}
                  data-help-target="mainapp.user.settings.resumegen"
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <SlidersHorizontal className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Resume Generation</span>
                  </div>
                  {optionsSectionOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {optionsSectionOpen && (
                  <YearLimitsSection
                    preferences={preferences}
                    onPreferencesChange={setPreferences}
                  />
                )}
              </div>

              {/* App Options Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setAppOptionsSectionOpen(!appOptionsSectionOpen)}
                  aria-expanded={appOptionsSectionOpen}
                  data-help-target="mainapp.user.settings.jobretention"
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Job Retention</span>
                  </div>
                  {appOptionsSectionOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {appOptionsSectionOpen && (
                  <JobRetentionSection
                    preferences={preferences}
                    onPreferencesChange={setPreferences}
                  />
                )}
              </div>

              {/* Security & Sign-in Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setSecuritySectionOpen(!securitySectionOpen)}
                  aria-expanded={securitySectionOpen}
                  data-help-target="mainapp.user.settings.security"
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  type="button"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                >
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Security & Sign-in</span>
                  </div>
                  {securitySectionOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {securitySectionOpen && (
                  <div className="p-3 space-y-4">
                    <PasswordChangeSection />
                    <div className="border-t border-gray-200 pt-4" data-help-target="mainapp.user.settings.security.emails">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Forwarding Email Addresses</h3>
                      <ForwardingEmailsSection />
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setSubscriptionSectionOpen(!subscriptionSectionOpen)}
                  aria-expanded={subscriptionSectionOpen}
                  data-help-target="mainapp.user.settings.subscription"
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Subscription</span>
                  </div>
                  {subscriptionSectionOpen ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {subscriptionSectionOpen && (
                  <div className="space-y-0">
                    <SubscriptionSection
                      preferences={preferences}
                      onPreferencesReload={loadPreferences}
                    />

                    {/* Separator */}
                    <div className="px-3">
                      <hr className="border-gray-200 my-4" />
                    </div>

                    {/* Organization Section */}
                    <div className="px-3 pb-3">
                      <OrganizationSection
                        preferences={preferences}
                        onPreferencesChange={setPreferences}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Communications */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-help-target="mainapp.user.settings.communications">
                <div className="p-3 space-y-3">
                  <div className="flex items-center mb-1">
                    <Mail className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Communications</span>
                  </div>
                  <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-700">Monthly Email Updates</span>
                      <span className="text-xs text-gray-500">Receive monthly product updates and tips from SkillSnap</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.marketing_email_optin || false}
                      onChange={(e) => setPreferences({ ...preferences, marketing_email_optin: e.target.checked })}
                      data-help-target="mainapp.user.settings.communications.emailupdates"
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col p-4 border-t space-y-3">
          {saveError && (
            <div role="alert" className="flex items-center p-2 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {saveError}
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <span role="status" aria-label="Saving">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                </span>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
